import { useEffect, useRef } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { Link } from 'react-router-dom';

function useScrollFade(ref: React.RefObject<HTMLElement>) {
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const children = (ref.current as HTMLElement)?.querySelectorAll('.fade-in-child');
            children?.forEach((child, i) => {
              const el = child as HTMLElement;
              setTimeout(() => {
                el.style.transition = 'opacity 0.7s ease, transform 0.7s ease';
                el.style.opacity = '1';
                el.style.transform = 'translateY(0)';
              }, i * 100);
            });
          }
        });
      },
      { threshold: 0.15 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [ref]);
}

const values = [
  { icon: 'ri-lightbulb-line', title: 'Innovation', desc: 'We push beyond conventional thinking to deliver forward-looking solutions that stand the test of time.' },
  { icon: 'ri-shake-hands-line', title: 'Integrity', desc: 'We operate with transparency and honesty in every relationship, with clients, partners, and communities.' },
  { icon: 'ri-bar-chart-2-line', title: 'Excellence', desc: 'High standards aren\'t negotiable. We hold ourselves accountable to quality on every deliverable.' },
  { icon: 'ri-global-line', title: 'Adaptability', desc: 'We evolve with the industries we serve, ensuring our capabilities remain cutting-edge and relevant.' },
];

export default function About() {
  const heroRef = useRef<HTMLDivElement>(null);
  const storyRef = useRef<HTMLDivElement>(null);
  const valuesRef = useRef<HTMLDivElement>(null);
  const ctaRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    document.title = 'About Us | Holston Development Group';
  }, []);

  useScrollFade(storyRef as React.RefObject<HTMLElement>);
  useScrollFade(valuesRef as React.RefObject<HTMLElement>);
  useScrollFade(ctaRef as React.RefObject<HTMLElement>);

  useEffect(() => {
    const el = heroRef.current;
    if (!el) return;
    const children = el.querySelectorAll('.hero-anim');
    children.forEach((child, i) => {
      const c = child as HTMLElement;
      c.style.opacity = '0';
      c.style.transform = 'translateY(28px)';
      setTimeout(() => {
        c.style.transition = 'opacity 0.8s ease, transform 0.8s ease';
        c.style.opacity = '1';
        c.style.transform = 'translateY(0)';
      }, 200 + i * 160);
    });
  }, []);

  return (
    <div className="min-h-screen">
      <Navbar />
      <main>
        {/* Hero */}
        <section className="relative min-h-[65vh] flex items-center justify-center overflow-hidden">
          <div className="absolute inset-0">
            <img
              src="https://public.readdy.ai/ai/img_res/edited_b397137668ce2a77b81eae99e4c55c6d_4b718b99.jpg"
              alt="Holston Development Group team"
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-[#001F4E]/75 via-[#001F4E]/65 to-[#001F4E]/80" />
          </div>
          <div ref={heroRef} className="relative z-10 w-full max-w-7xl mx-auto px-6 lg:px-10 text-center pt-20">
            <p className="hero-anim text-[#00A3E0] text-sm font-semibold uppercase tracking-widest mb-5">Our Story</p>
            <h1 className="hero-anim text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-tight">
              About <span className="text-[#00A3E0]">Holston Development Group</span>
            </h1>
            <p className="hero-anim text-white/80 text-lg max-w-2xl mx-auto mt-6 leading-relaxed">
              Decades of expertise across multiple industries, all under one group. Built to perform, structured to adapt.
            </p>
          </div>
        </section>

        {/* Story & Mission */}
        <section ref={storyRef} className="py-20 lg:py-28 bg-white">
          <div className="max-w-7xl mx-auto px-6 lg:px-10">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-14 items-center">
              <div>
                <p className="fade-in-child text-[#0050B2] text-sm font-semibold uppercase tracking-widest mb-3" style={{ opacity: 0, transform: 'translateY(20px)' }}>Who We Are</p>
                <h2 className="fade-in-child text-3xl lg:text-4xl font-bold text-[#001F4E] mb-6" style={{ opacity: 0, transform: 'translateY(20px)' }}>
                  Decades of Experience.<br /><span className="text-[#0050B2]">One Unified Vision.</span>
                </h2>
                <p className="fade-in-child text-gray-600 text-base leading-relaxed mb-4" style={{ opacity: 0, transform: 'translateY(20px)' }}>
                  Holston Development Group is a multi-discipline company built on depth, precision, and results. With over 60 years of combined leadership experience spanning telecommunications, engineering, construction, and land services, we bring a depth of knowledge to every engagement.
                </p>
                <p className="fade-in-child text-gray-600 text-base leading-relaxed mb-4" style={{ opacity: 0, transform: 'translateY(20px)' }}>
                  Our multi-division structure allows each practice area to operate with specialized focus while benefiting from the resources and knowledge of the broader group. Clients get the attention of a small firm with the capability of a larger one.
                </p>
                <p className="fade-in-child text-gray-600 text-base leading-relaxed" style={{ opacity: 0, transform: 'translateY(20px)' }}>
                  We&apos;re not a traditional company, and that&apos;s by design.
                </p>
              </div>
              <div className="grid grid-cols-2 gap-5">
                {[
                  { value: '60+', label: 'Years Combined Experience', icon: 'ri-medal-line' },
                  { value: '20+', label: 'States Nationwide', icon: 'ri-map-pin-line' },
                  { value: 'Multi', label: 'Industry Divisions', icon: 'ri-layout-grid-line' },
                  { value: '100%', label: 'Client-Focused Approach', icon: 'ri-focus-3-line' },
                ].map((stat, i) => (
                  <div key={i} className="fade-in-child bg-[#E8EFF6] rounded-lg p-6 text-center" style={{ opacity: 0, transform: 'translateY(20px)' }}>
                    <div className="w-10 h-10 flex items-center justify-center mx-auto mb-3">
                      <i className={`${stat.icon} text-2xl text-[#0050B2]`} />
                    </div>
                    <p className="text-2xl font-bold text-[#001F4E] mb-1">{stat.value}</p>
                    <p className="text-xs text-gray-500 font-medium leading-snug">{stat.label}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Mission */}
        <section className="py-16 bg-[#0050B2]">
          <div className="max-w-4xl mx-auto px-6 lg:px-10 text-center">
            <div className="w-12 h-12 flex items-center justify-center mx-auto mb-6">
              <i className="ri-compass-3-line text-3xl text-white/70" />
            </div>
            <h2 className="text-2xl lg:text-3xl font-bold text-white mb-4">Our Mission</h2>
            <p className="text-white/85 text-lg leading-relaxed">
              To deliver results across multiple industries by combining hands-on expertise with the flexibility to meet every challenge. We don&apos;t just manage projects, we move them forward.
            </p>
          </div>
        </section>

        {/* Values */}
        <section ref={valuesRef} className="py-20 lg:py-28 bg-white">
          <div className="max-w-7xl mx-auto px-6 lg:px-10">
            <div className="text-center mb-12">
              <p className="fade-in-child text-[#0050B2] text-sm font-semibold uppercase tracking-widest mb-3" style={{ opacity: 0, transform: 'translateY(20px)' }}>What Drives Us</p>
              <h2 className="fade-in-child text-3xl lg:text-4xl font-bold text-[#001F4E]" style={{ opacity: 0, transform: 'translateY(20px)' }}>Our Core Values</h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {values.map((v, i) => (
                <div key={i} className="fade-in-child p-6 rounded-lg border border-[#E8EFF6] hover:border-[#0050B2]/30 hover:bg-[#E8EFF6]/40 transition-all duration-300" style={{ opacity: 0, transform: 'translateY(20px)' }}>
                  <div className="w-11 h-11 flex items-center justify-center rounded-md bg-[#E8EFF6] mb-4">
                    <i className={`${v.icon} text-xl text-[#0050B2]`} />
                  </div>
                  <h3 className="text-[#001F4E] font-bold text-base mb-2">{v.title}</h3>
                  <p className="text-gray-500 text-sm leading-relaxed">{v.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section ref={ctaRef} className="py-20 bg-[#E8EFF6]">
          <div className="max-w-7xl mx-auto px-6 lg:px-10">
            <div className="fade-in-child bg-[#001F4E] rounded-lg px-8 py-12 lg:px-14 text-center" style={{ opacity: 0, transform: 'translateY(20px)' }}>
              <h3 className="text-white text-2xl lg:text-3xl font-bold mb-3">Let&apos;s Build Something Together</h3>
              <p className="text-white/70 text-base mb-8 max-w-xl mx-auto">Reach out to our team and discover how Holston Development Group can bring its expertise to your next project.</p>
              <Link to="/contact" className="inline-block px-8 py-3.5 rounded-md bg-[#0050B2] text-white font-semibold text-sm hover:bg-[#00A3E0] transition-colors duration-200 cursor-pointer whitespace-nowrap">
                Contact Us
              </Link>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
